import React from 'react';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  ExportNotificationMethod,
  ExportScreeningFrequency,
} from 'api/ExportHubAPI';
import LensMultiSelect from './LensMultiSelect';
import StyledSingleSelect from './StyledSingleSelect';

export const FREQUENCIES: Array<{
  value: ExportScreeningFrequency;
  label: string;
}> = [
  { value: '', label: 'Off (manual only)' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Annually' },
];

export const NOTIFY_METHODS: Array<{
  value: ExportNotificationMethod;
  label: string;
  description: string;
}> = [
  { value: 'in_app', label: 'In app', description: 'Notifications appear in BITE.' },
  { value: 'email', label: 'Email', description: 'Send to a notification email.' },
  { value: 'both', label: 'Both', description: 'In app and email.' },
  { value: 'none', label: 'None', description: 'No notifications.' },
];

export const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const DAY_OPTIONS = DAYS.map((d) => ({ value: d, label: d }));

interface Props {
  frequency: ExportScreeningFrequency;
  setFrequency: (value: ExportScreeningFrequency) => void;
  dayOfWeek: string;
  setDayOfWeek: (value: string) => void;
  method: ExportNotificationMethod;
  setMethod: (value: ExportNotificationMethod) => void;
  email: string;
  setEmail: (value: string) => void;
  lenses: string[];
  setLenses: (value: string[]) => void;
  availableLenses: string[];
  lensesLoading?: boolean;
  disabled?: boolean;
}

/**
 * Shared screening-settings field set — used both by the dashboard
 * "Screening Settings" section and the Create Database wizard so the two stay in
 * lockstep. Frequency and lenses use the Export-Screening styled dropdowns
 * (side by side at the top); notification method is a card-style radio group.
 * All dropdown menus render in-flow so they never run off the bottom of the
 * page (dashboard) or clip inside the modal body.
 */
const ScreeningSettingsFields: React.FC<Props> = ({
  frequency,
  setFrequency,
  dayOfWeek,
  setDayOfWeek,
  method,
  setMethod,
  email,
  setEmail,
  lenses,
  setLenses,
  availableLenses,
  lensesLoading = false,
  disabled = false,
}) => {
  const emailNeeded = method === 'email' || method === 'both';

  return (
    <div className='eh-settings-fields'>
      <div className='eh-settings-grid'>
        <div className='eh-field'>
          <StyledSingleSelect
            label='Screening frequency'
            icon={<ScheduleOutlinedIcon style={{ fontSize: 18 }} />}
            options={FREQUENCIES}
            value={frequency}
            onChange={(v) => setFrequency(v as ExportScreeningFrequency)}
            disabled={disabled}
            inline
          />
        </div>

        {frequency === 'Weekly' && (
          <div className='eh-field'>
            <StyledSingleSelect
              label='Day of week'
              icon={<CalendarTodayOutlinedIcon style={{ fontSize: 18 }} />}
              options={DAY_OPTIONS}
              value={dayOfWeek}
              onChange={setDayOfWeek}
              disabled={disabled}
              inline
            />
          </div>
        )}

        <div className='eh-field'>
          <LensMultiSelect
            label='BITE Lenses to screen against'
            options={availableLenses}
            selected={lenses}
            onChange={setLenses}
            loading={lensesLoading}
            disabled={disabled}
            inline
          />
        </div>
      </div>

      <div className='eh-field'>
        <span className='eh-field-label'>Notify me via</span>
        <div className='eh-radio-cards' role='radiogroup' aria-label='Notify me via'>
          {NOTIFY_METHODS.map((m) => {
            const checked = method === m.value;
            return (
              <div
                key={m.value}
                role='radio'
                aria-checked={checked}
                aria-label={m.label}
                tabIndex={disabled ? -1 : 0}
                className={`eh-radio-card${checked ? ' active' : ''}`}
                onClick={() => !disabled && setMethod(m.value)}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setMethod(m.value);
                  }
                }}
              >
                <span
                  aria-hidden='true'
                  className={`ce-radio${checked ? ' ce-radio--checked' : ''}`}
                >
                  <span className='ce-radio__dot' />
                </span>
                <div>
                  <div className='eh-radio-card-title'>{m.label}</div>
                  <div className='eh-radio-card-desc'>{m.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {emailNeeded && (
        <label className='eh-field'>
          <span className='eh-field-label'>Notification email</span>
          <input
            type='email'
            className='eh-text-input'
            value={email}
            placeholder='name@company.com'
            disabled={disabled}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
      )}
    </div>
  );
};

export default ScreeningSettingsFields;
